import { useEffect, useState } from "react";
import ModalComponent from "../../../../../../components/modals/ModalFormComponent";
import Label from "../../../../../../components/form_fields/Label";
import { SubmitHandler, useForm } from "react-hook-form";
import {
	useCreatePension,
	useUpdatePension,
} from "../../../../../../services/pension.service";
import {
	CreatePensionTierDto,
	UpdatePensionTierDto,
	PENSION_CONTRIBUTION_FORMULA_TYPE,
	DeletePensionTierDto,
} from "../../../../../../models/pension.dto";
import useEditFormStore from "../../../../../../state-management/useEditFormStore";
import {
	CustomizedTooltip,
	formatRate,
	IAddEditTableForm,
} from "../../../../../../helpers/appHelpers";
import FormDescriptionInput from "../../../../../../components/form_fields/FormDescriptionInput";
import MoneyInput from "../../../../../../components/form_fields/MoneyInput";
import OrangeButton from "../../../../../../components/buttons/OrangeButton";
import { useManageTableWithSearchParamsData } from "../../../../../../helpers/useManageTable";
import useCompanyStore from "../../../../../../state-management/useCompanyStore";
import { CountryDto } from "../../../../../../models/country.dto";
import FormCodeInput from "../../../../../../components/form_fields/FormCodeInput";
import RateComponent from "../../../../../../components/form_fields/RateComponent";
import { AiOutlinePlus } from "react-icons/ai";
import { BsTrash } from "react-icons/bs";
import { Typography } from "@mui/material";

interface IFormInputs {
	countryId: number;
	name: string;
	code: string;
	description: string;
	employeeContributionFormulaType: PENSION_CONTRIBUTION_FORMULA_TYPE;
	employeeFixedComponent: number;
	employeeRateComponent: number;
	employerContributionFormulaType: PENSION_CONTRIBUTION_FORMULA_TYPE;
	employerFixedComponent: number;
	employerRateComponent: number;
	minimumInsurableIncome?: number;
	maximumInsurableIncome?: number;
	active?: string;
	tiers?: CreatePensionTierDto[];
	tiersToCreate?: CreatePensionTierDto[];
	tiersToUpdate?: UpdatePensionTierDto[];
	tiersToDelete?: DeletePensionTierDto[];
}

type PensionModalFormProps = IAddEditTableForm & {
	countries: CountryDto[];
	reloadTableData?: () => void;
};

const PensionModalForm = ({
	open,
	setOpen,
	title,
	isEditForm,
	tableAPIRef,
	countries,
	reloadTableData,
}: PensionModalFormProps) => {
	const { currentCompany } = useCompanyStore();
	const { formDataValues } = useEditFormStore();
	const [pensionTierCount, setPensionTierCount] = useState<number>(
		isEditForm ? formDataValues?.tiers?.length : 0,
	);
	const [uploading, setUploading] = useState(false);

	const {
		register,
		watch,
		handleSubmit,
		getValues,
		setValue,
		reset,
		formState: { errors },
	} = useForm<IFormInputs>({
		defaultValues: {
			name: isEditForm ? formDataValues?.name : undefined,
			code: isEditForm ? formDataValues?.code : undefined,
			description: isEditForm ? formDataValues?.description : undefined,
			employeeContributionFormulaType: isEditForm
				? formDataValues?.employeeContributionFormulaType
				: undefined,
			employeeFixedComponent: isEditForm
				? formDataValues?.employeeFixedComponent
				: undefined,
			employeeRateComponent: isEditForm
				? Number(formDataValues?.employeeRateComponent) * 100
				: undefined,
			employerContributionFormulaType: isEditForm
				? formDataValues?.employerContributionFormulaType
				: undefined,
			employerFixedComponent: isEditForm
				? formDataValues?.employerFixedComponent
				: undefined,
			employerRateComponent: isEditForm
				? Number(formDataValues?.employerRateComponent) * 100
				: undefined,
			minimumInsurableIncome: isEditForm
				? formDataValues?.minimumInsurableIncome
				: undefined,
			maximumInsurableIncome: isEditForm
				? formDataValues?.maximumInsurableIncome
				: undefined,
			active: isEditForm ? (formDataValues?.active ? "yes" : "no") : undefined,
			tiersToUpdate: isEditForm
				? formDataValues?.tiers?.map((tier) => {
						delete tier.pensionId;
						delete tier.createdAt;
						delete tier.modifiedAt;
						tier.percentage = getPercentage(tier.percentage);
						return tier;
					})
				: undefined,
		},
	});

	const tiersToUpdate = watch("tiersToUpdate") || [];
	const tiersToDelete = watch("tiersToDelete") || [];
	const employeeContributionFormulaType = watch(
		"employeeContributionFormulaType",
	);
	const employerContributionFormulaType = watch(
		"employerContributionFormulaType",
	);

	const deleteCurrentTier = (index: number, id?: number) => {
		const updatedTiers = tiersToUpdate.filter((_, i) => i !== index);

		if (id) {
			reset({
				...getValues(),
				tiersToUpdate: updatedTiers,
				tiersToDelete: [...tiersToDelete, { id }],
			});
		} else {
			reset({ ...getValues(), tiersToUpdate: updatedTiers });
		}

		setPensionTierCount((prev) => prev - 1);
	};

	const addNewTier = () => {
		if (isEditForm || formDataValues?.tiers?.length) {
			const updatedTiers = tiersToUpdate.slice(0, pensionTierCount);
			setValue("tiersToUpdate", updatedTiers);
		}
		setPensionTierCount((prev) => prev + 1);
	};

	useEffect(() => {
		if (employeeContributionFormulaType === "FLAT") {
			setValue("employeeRateComponent", 0);
		} else if (employeeContributionFormulaType === "PERCENT") {
			setValue("employeeFixedComponent", 0);
		}
	}, [employeeContributionFormulaType]);

	useEffect(() => {
		if (employerContributionFormulaType === "FLAT") {
			setValue("employerRateComponent", 0);
		} else if (employerContributionFormulaType === "PERCENT") {
			setValue("employeeFixedComponent", 0);
		}
	}, [employerContributionFormulaType]);

	const { mutate: createPension } = useCreatePension();
	const { mutate: updatePension } = useUpdatePension();
	const { addNewTableData } = useManageTableWithSearchParamsData();

	const onSubmit: SubmitHandler<IFormInputs> = (data: any) => {
		setUploading(true);

		formatRate(data, "employeeRateComponent");
		formatRate(data, "employerRateComponent");
		data.active = data.active === "yes" ? true : false;

		if (isEditForm) {
			if (pensionTierCount) {
				if (!formDataValues?.tiers?.length) {
					data.tiersToCreate = data.tiers
						.slice(0, pensionTierCount)
						.map((tier) => filterPayloadData(tier));
					delete data.tiers;
				} else {
					const validTiers = data.tiersToUpdate.slice(0, pensionTierCount);
					data.tiersToUpdate = validTiers
						.filter((tier) => tier.id)
						.map((tier) => filterPayloadData(tier));
					data.tiersToCreate = validTiers
						.filter((tier) => !tier.id)
						.map((tier) => filterPayloadData(tier));
				}
			}

			updatePension(
				{ payload: data, id: formDataValues?.id },
				{
					onSuccess: (updatedData) => {
						try {
							tableAPIRef?.current.updateRows([updatedData.data]);
						} catch (error) {
							console.error("Error updating table rows:", error);
							reloadTableData?.();
						}
						setOpen(false);
						reset();
					},
					onSettled: () => setUploading(false),
				},
			);
		} else {
			if (pensionTierCount) {
				data.tiers = data.tiers
					.slice(0, pensionTierCount)
					.map((tier) => filterPayloadData(tier));
			}

			createPension(
				{
					payload: {
						...data,
						companyId: currentCompany?.id,
					},
				},
				{
					onSuccess: () => {
						addNewTableData();
						setOpen(false);
						reset();
					},
					onSettled: () => setUploading(false),
				},
			);
		}
	};

	const handleClose = () => {
		setOpen(false);
		reset();
	};

	return (
		<ModalComponent open={open} title={title} handleClose={handleClose}>
			<form onSubmit={handleSubmit(onSubmit)}>
				<div className="grid grid-cols-2 gap-x-4 gap-y-6">
					<div className="w-full">
						<Label title="Name" for="name" />
						<input
							placeholder={"Enter name"}
							type="text"
							{...register("name", {
								required: "Enter name here",
							})}
							id="name"
							className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm  ${
								errors.name
									? " border border-red-500 focus:border-red-500 focus:outline-red-500"
									: "border-none"
							}`}
						/>
						{errors.name && (
							<p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
						)}
					</div>
					<div className="w-full">
						<Label title="Unique Code" for="code" />
						<FormCodeInput
							register={register}
							attributes={{
								className: `w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
									errors.code
										? " border border-red-500 focus:border-red-500 focus:outline-red-500"
										: "border-none"
								}`,
							}}
						/>
						{errors.code && (
							<p className="text-red-500 text-sm mt-1">{errors.code.message}</p>
						)}
					</div>
					<div className="w-full col-span-2">
						<Label title="Description" for="description" />
						<FormDescriptionInput
							register={register}
							attributes={{
								defaultValue: isEditForm ? formDataValues?.description : "",
								className: `w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
									errors.description
										? " border border-red-500 focus:border-red-500 focus:outline-red-500"
										: "border-none"
								}`,
							}}
						/>
						{errors.description && (
							<p className="text-red-500 text-sm mt-1">
								{errors.description.message}
							</p>
						)}
					</div>
					<div className="w-full">
						<Label
							title="Min Insurable Income"
							for="minimumInsurableIncome"
							optional
						/>
						<MoneyInput
							attributes={{
								id: "minimumInsurableIncome",
								placeholder: "0",
							}}
							register={register}
							name="minimumInsurableIncome"
							setValue={setValue}
							defaultValue={
								isEditForm ? formDataValues?.minimumInsurableIncome : undefined
							}
							required={false}
							error={errors.minimumInsurableIncome}
						/>
					</div>
					<div className="w-full">
						<Label
							title="Max Insurable Income"
							for="maximumInsurableIncome"
							optional
						/>
						<MoneyInput
							attributes={{
								id: "maximumInsurableIncome",
								placeholder: "0",
							}}
							register={register}
							name="maximumInsurableIncome"
							setValue={setValue}
							defaultValue={
								isEditForm ? formDataValues?.maximumInsurableIncome : undefined
							}
							required={false}
							error={errors.maximumInsurableIncome}
						/>
					</div>
					{!isEditForm && (
						<div className="w-full">
							<Label title="Country" for="countryId" />
							<select
								className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm mb-2 ${
									errors.countryId
										? " border border-red-500 focus:border-red-500 focus:outline-red-500"
										: "border-none"
								}`}
								{...register("countryId", {
									required: "Select country here",
									valueAsNumber: true,
								})}
							>
								<option value="">Select Country</option>
								{countries.map((country) => (
									<option key={country.id} value={country.id}>
										{country.name}
									</option>
								))}
							</select>
							{errors.countryId && (
								<p className="text-red-500 text-sm mt-1">
									{errors.countryId.message}
								</p>
							)}
						</div>
					)}
				</div>
				<div className="mt-6">
					<h3 className="font-semibold text-base mb-2">
						Employee Contribution
					</h3>
					<div className="grid grid-cols-2 gap-x-4 gap-y-6">
						<div className="w-full">
							<Label
								title="Formula Type"
								for="employeeContributionFormulaType"
							/>
							<select
								className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
									errors.employeeContributionFormulaType
										? " border border-red-500 focus:border-red-500 focus:outline-red-500"
										: "border-none"
								}`}
								{...register("employeeContributionFormulaType", {
									required: "Select employee contribution formula type here",
								})}
							>
								<option value="">Select Formula Type</option>
								<option value="FLAT">Flat</option>
								<option value="PERCENT">Percent</option>
								<option value="HYBRID">Hybrid</option>
							</select>
							{errors.employeeContributionFormulaType && (
								<p className="text-red-500 text-sm mt-1">
									{errors.employeeContributionFormulaType.message}
								</p>
							)}
						</div>
						<div></div>
						<div className="w-full">
							<Label title="Fixed Component" for="employeeFixedComponent" />
							<MoneyInput
								attributes={{
									id: "employeeFixedComponent",
									placeholder: "0",
									disabled: employeeContributionFormulaType === "PERCENT",
								}}
								register={register}
								name="employeeFixedComponent"
								setValue={setValue}
								defaultValue={
									isEditForm
										? formDataValues?.employeeFixedComponent
										: undefined
								}
								clearValue={employeeContributionFormulaType === "PERCENT"}
								required={
									employeeContributionFormulaType === "PERCENT"
										? false
										: "Enter employee fixed component"
								}
								error={errors.employeeFixedComponent}
							/>
							{errors.employeeFixedComponent && (
								<p className="text-red-500 text-sm mt-1">
									{errors.employeeFixedComponent.message}
								</p>
							)}
						</div>
						<div className="w-full">
							<Label title="Rate Component" for="employeeRateComponent" />
							<RateComponent
								attributes={{
									id: "employeeRateComponent",
									disabled: Boolean(employeeContributionFormulaType === "FLAT"),
								}}
								register={register}
								name="employeeRateComponent"
								required={
									employeeContributionFormulaType === "FLAT"
										? false
										: "Enter employee rate component"
								}
								error={errors.employeeRateComponent}
							/>
							{errors.employeeRateComponent && (
								<p className="text-red-500 text-sm mt-1">
									{errors.employeeRateComponent.message}
								</p>
							)}
						</div>
					</div>
				</div>
				<div className="mt-6">
					<h3 className="font-semibold text-base mb-2">
						Employer Contribution
					</h3>
					<div className="grid grid-cols-2 gap-x-4 gap-y-6">
						<div className="w-full">
							<Label
								title="Formula Type"
								for="employerContributionFormulaType"
							/>
							<select
								className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
									errors.employerContributionFormulaType
										? " border border-red-500 focus:border-red-500 focus:outline-red-500"
										: "border-none"
								}`}
								{...register("employerContributionFormulaType", {
									required: "Select employer contribution formula type here",
								})}
							>
								<option value="">Select Formula Type</option>
								<option value="FLAT">Flat</option>
								<option value="PERCENT">Percent</option>
								<option value="HYBRID">Hybrid</option>
							</select>
							{errors.employerContributionFormulaType && (
								<p className="text-red-500 text-sm mt-1">
									{errors.employerContributionFormulaType.message}
								</p>
							)}
						</div>
						<div></div>
						<div className="w-full">
							<Label title="Fixed Component" for="employerFixedComponent" />
							<MoneyInput
								attributes={{
									id: "employerFixedComponent",
									placeholder: "0",
									disabled: employerContributionFormulaType === "PERCENT",
								}}
								register={register}
								name="employerFixedComponent"
								setValue={setValue}
								defaultValue={
									isEditForm
										? formDataValues?.employerFixedComponent
										: undefined
								}
								clearValue={employerContributionFormulaType === "PERCENT"}
								required={
									employerContributionFormulaType === "PERCENT"
										? false
										: "Enter employer fixed component"
								}
								error={errors.employerFixedComponent}
							/>
							{errors.employerFixedComponent && (
								<p className="text-red-500 text-sm mt-1">
									{errors.employerFixedComponent.message}
								</p>
							)}
						</div>
						<div className="w-full">
							<Label title="Rate Component" for="employerRateComponent" />
							<RateComponent
								attributes={{
									id: "employerRateComponent",
									disabled: Boolean(employerContributionFormulaType === "FLAT"),
								}}
								register={register}
								name="employerRateComponent"
								required={
									employerContributionFormulaType === "FLAT"
										? false
										: "Enter employer rate component"
								}
								error={errors.employerRateComponent}
							/>
							{errors.employerRateComponent && (
								<p className="text-red-500 text-sm mt-1">
									{errors.employerRateComponent.message}
								</p>
							)}
						</div>
						<div className="col-span-2">
							<div className="flex space-x-12 text-base">
								<h3>Active</h3>
								<div className="flex space-x-3 items-center">
									<input
										{...register("active")}
										type="radio"
										value={"yes"}
										id="active"
									/>
									<label htmlFor="active">Yes</label>
								</div>
								<div className="flex space-x-3 items-center">
									<input
										{...register("active")}
										type="radio"
										value={"no"}
										id="inactive"
									/>
									<label htmlFor="inactive">No</label>
								</div>
							</div>
							{errors.active && (
								<p className="text-red-500 text-sm mt-1">
									{errors.active.message}
								</p>
							)}
						</div>
					</div>
				</div>
				<div className="mt-10">
					<h6 className="font-bold text-base mb-2 flex items-center gap-1.5">
						Add Pension Tiers
						<i className="font-semibold">(optional)</i>
					</h6>
					{!isEditForm || !formDataValues?.tiers?.length ? (
						<div className="grid grid-cols-1 gap-y-6 mt-3">
							{Array.from({ length: pensionTierCount || 0 }).map((_, index) => (
								<div key={index} className="flex item s-center gap-4">
									<h6 className="font-bold whitespace-nowrap text-sm flex flex-col gap-3">
										<p>Tier {index + 1}</p>
										{index + 1 === pensionTierCount && (
											<button
												type="button"
												onClick={() => setPensionTierCount((prev) => prev - 1)}
											>
												<BsTrash className="text-xl hover:scale-[1.1]" />
											</button>
										)}
									</h6>
									<div className="grid grid-cols-2 gap-y-4 gap-x-3">
										<div className="w-full">
											<Label title="Name" for={"tiers.${index}.name"} />
											<input
												placeholder={"Enter name"}
												type="text"
												{...register(`tiers.${index}.name`, {
													required: "Enter name here",
												})}
												id={"tiers.${index}.name"}
												className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm  ${
													errors.tiers && errors.tiers[index]?.name
														? " border border-red-500 focus:border-red-500 focus:outline-red-500"
														: "border-none"
												}`}
											/>
											{errors.tiers && errors.tiers[index]?.name && (
												<p className="text-red-500 text-sm mt-1">
													{errors.tiers[index]?.name?.message}
												</p>
											)}
										</div>
										<div className="w-full">
											<Label
												title="Percentage"
												for={`tiers.${index}.percentage`}
											/>
											<RateComponent
												attributes={{
													id: `tiers.${index}.percentage`,
													max: 100,
												}}
												register={register}
												name={`tiers.${index}.percentage`}
												required={"Enter percentage here"}
												error={errors.tiers && errors.tiers[index]?.percentage}
											/>
											{errors.tiers && errors.tiers[index]?.percentage && (
												<p className="text-red-500 text-sm mt-1">
													{errors.tiers[index]?.percentage?.message}
												</p>
											)}
										</div>
										<div className="w-full">
											<Label
												title="Description"
												for={`tiers.${index}.description`}
												optional
											/>
											<input
												placeholder={"Enter brief description"}
												type="text"
												{...register(`tiers.${index}.description`, {
													required: false,
												})}
												id={`tiers.${index}.description`}
												className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm`}
											/>
										</div>
										<div className="w-full">
											<Label
												title="Sequence Number"
												for={`tiers.${index}.sequenceNumber`}
												optional
											/>
											<input
												placeholder={"Enter number"}
												type="number"
												{...register(`tiers.${index}.sequenceNumber`, {
													required: false,
													valueAsNumber: true,
												})}
												id={`tiers.${index}.sequenceNumber`}
												min={1}
												max={255}
												className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm`}
											/>
										</div>
										<div className="w-full">
											<Label
												title="Minimum Payable"
												for={`tiers.${index}.minimumPayable`}
												optional
											/>
											<MoneyInput
												attributes={{
													id: `tiers.${index}.minimumPayable`,
													placeholder: "0",
												}}
												register={register}
												name={`tiers.${index}.minimumPayable`}
												setValue={setValue}
												required={false}
											/>
										</div>
										<div className="w-full">
											<Label
												title="Maximum Payable"
												for={`tiers.${index}.maximumPayable`}
												optional
											/>
											<MoneyInput
												attributes={{
													id: `tiers.${index}.maximumPayable`,
													placeholder: "0",
												}}
												register={register}
												name={`tiers.${index}.maximumPayable`}
												setValue={setValue}
												required={false}
											/>
										</div>
									</div>
								</div>
							))}
						</div>
					) : (
						<div className="grid grid-cols-1 gap-y-6 mt-3">
							{Array.from({ length: pensionTierCount || 0 }).map((_, index) => (
								<div key={index} className="flex item s-center gap-4">
									<h6 className="font-bold whitespace-nowrap text-sm flex flex-col gap-3">
										<p>Tier {index + 1}</p>
										<CustomizedTooltip
											title="This action is irreversible!"
											placement="top"
											arrow
										>
											<Typography>
												<button
													type="button"
													onClick={() =>
														deleteCurrentTier(
															index,
															tiersToUpdate[index]?.id || undefined,
														)
													}
												>
													<BsTrash className="text-xl hover:scale-[1.1]" />
												</button>
											</Typography>
										</CustomizedTooltip>
									</h6>
									<div className="grid grid-cols-2 gap-y-4 gap-x-3">
										<div className="w-full">
											<Label title="Name" for={"tiersToUpdate.${index}.name"} />
											<input
												placeholder={"Enter name"}
												type="text"
												{...register(`tiersToUpdate.${index}.name`, {
													required: "Enter name here",
												})}
												id={"tiersToUpdate.${index}.name"}
												className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm  ${
													errors.tiersToUpdate &&
													errors.tiersToUpdate[index]?.name
														? " border border-red-500 focus:border-red-500 focus:outline-red-500"
														: "border-none"
												}`}
											/>
											{errors.tiersToUpdate &&
												errors.tiersToUpdate[index]?.name && (
													<p className="text-red-500 text-sm mt-1">
														{errors.tiersToUpdate[index]?.name?.message}
													</p>
												)}
										</div>
										<div className="w-full">
											<Label
												title="Percentage"
												for={`tiersToUpdate.${index}.percentage`}
											/>
											<RateComponent
												attributes={{
													id: `tiersToUpdate.${index}.percentage`,
													max: 100,
												}}
												register={register}
												name={`tiersToUpdate.${index}.percentage`}
												required={"Enter percentage here"}
												error={
													errors.tiersToUpdate &&
													errors.tiersToUpdate[index]?.percentage
												}
											/>
											{errors.tiersToUpdate &&
												errors.tiersToUpdate[index]?.percentage && (
													<p className="text-red-500 text-sm mt-1">
														{errors.tiersToUpdate[index]?.percentage?.message}
													</p>
												)}
										</div>
										<div className="w-full">
											<Label
												title="Description"
												for={`tiersToUpdate.${index}.description`}
												optional
											/>
											<input
												placeholder={"Enter brief description"}
												type="text"
												{...register(`tiersToUpdate.${index}.description`, {
													required: false,
												})}
												id={`tiersToUpdate.${index}.description`}
												className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm`}
											/>
										</div>
										<div className="w-full">
											<Label
												title="Sequence Number"
												for={`tiersToUpdate.${index}.sequenceNumber`}
												optional
											/>
											<input
												placeholder={"Enter number"}
												type="number"
												{...register(`tiersToUpdate.${index}.sequenceNumber`, {
													required: false,
													valueAsNumber: true,
												})}
												id={`tiersToUpdate.${index}.sequenceNumber`}
												min={1}
												max={255}
												className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm`}
											/>
										</div>
										<div className="w-full">
											<Label
												title="Minimum Payable"
												for={`tiersToUpdate.${index}.minimumPayable`}
												optional
											/>
											<MoneyInput
												attributes={{
													id: `tiersToUpdate.${index}.minimumPayable`,
													placeholder: "0",
												}}
												register={register}
												name={`tiersToUpdate.${index}.minimumPayable`}
												setValue={setValue}
												defaultValue={
													isEditForm
														? formDataValues?.tiers[index]?.minimumPayable
														: undefined
												}
												required={false}
											/>
										</div>
										<div className="w-full">
											<Label
												title="Maximum Payable"
												for={`tiersToUpdate.${index}.maximumPayable`}
												optional
											/>
											<MoneyInput
												attributes={{
													id: `tiersToUpdate.${index}.maximumPayable`,
													placeholder: "0",
												}}
												register={register}
												name={`tiersToUpdate.${index}.maximumPayable`}
												setValue={setValue}
												defaultValue={
													isEditForm
														? formDataValues?.tiers[index]?.maximumPayable
														: undefined
												}
												required={false}
											/>
										</div>
									</div>
								</div>
							))}
						</div>
					)}
					<div
						className="w-fit flex items-center text-primary-blue cursor-pointer mt-4"
						onClick={addNewTier}
					>
						<AiOutlinePlus className="mr-1 text-base" />
						<span className="text-sm">Add New</span>
					</div>
				</div>

				<div className="w-full flex justify-end mt-8">
					<OrangeButton
						title={
							uploading
								? "Uploading..."
								: isEditForm
									? "Update Pension Scheme"
									: "Add Pension Scheme"
						}
						type="submit"
						disabled={uploading}
						className="bg-primary-mango text-white h-14 px-8 rounded-lg text-sm"
					/>
				</div>
			</form>
		</ModalComponent>
	);
};

export default PensionModalForm;

function filterPayloadData(data: any) {
	if (data) {
		Object.entries(data).forEach(([key, value]) => {
			if (typeof value === "string") {
				data[key] = value.trim();
			}
			if (value !== 0 && !value) {
				delete data[key];
			}
			if (key === "percentage") {
				data[key] = (value as number) / 100;
			}
		});
	}

	return data;
}

function getPercentage(decimalString: string): string {
	const parts = decimalString.split(".");

	if (parts?.length === 1 && parts[0] === "0") {
		return "0";
	}
	if (parts?.length === 1) {
		return parts[0];
	}

	if (parts[1]?.length === 1) {
		return parts[1] === "1" ? "100" : `${parts[1]}0`;
	} else {
		if (parts[1]?.slice(2)) {
			return `${parts[1].slice(0, 2)}.${parts[1].slice(2)}`;
		}
		return parts[1].slice(0, 1) === "0" ? parts[1].slice(1, 2) : `${parts[1]}`;
	}
}
